import { DateTime, Info } from 'luxon';
import { AdapterLuxon as LuxonUtils } from '@mui/x-date-pickers/AdapterLuxon';

export const weekUris = [
  'urn:replicon:day-of-week:monday',
  'urn:replicon:day-of-week:tuesday',
  'urn:replicon:day-of-week:wednesday',
  'urn:replicon:day-of-week:thursday',
  'urn:replicon:day-of-week:friday',
  'urn:replicon:day-of-week:saturday',
  'urn:replicon:day-of-week:sunday'
];
export const DEFAULT_WEEKDAY = 'urn:replicon:day-of-week:sunday';

export type WeekDay = typeof weekUris[number];

export const getFirstDayOfWeek = (weekDayUri: WeekDay): number =>
  weekUris.indexOf(weekDayUri) + 1;

const rotateRight = (arr: any[]) => [
  arr[arr.length - 1],
  ...arr.slice(0, arr.length - 1)
];
const rotateLeft = (arr: any[]) => [...arr.slice(1, arr.length), arr[0]];

// Repeats a function call (f) against a given input (x) multiple (n) times.
//  e.g. repeat(10)(x => x + 1)(0) = 10
const repeat = (n: number) => (f: (x: any) => any) => (x: any): any =>
  n === 0 ? x : repeat(n - 1)(f)(f(x));

// Localized week start (Luxon is 1 based and 1 means Monday, weekstart is 0 based and 0 means Sunday)
const luxonWeekdayToWeekstartWeekday = (weekday: number) => weekday % 7;
const weekstartWeekdayToLuxonWeekday = (weekday: number) =>
  ((weekday + 7 - 1) % 7) + 1;

/**
 * Overrides the Luxon utils to provide the monthly calendar according
 * to the specification of the web-timesheet user work-week schedule.
 */

export const createCustomLuxonUtils = (workWeekStartUri: WeekDay): any => {
  class CustomLuxonUtils extends LuxonUtils {
    readonly getWeekdays = (): string[] => {
      // Localized week start
      const weekStart = weekUris.indexOf(workWeekStartUri) + 1;

      // Normalize the luxon weekdays
      const normalizedWeekdays = rotateRight(
        Info.weekdaysFormat('short', { locale: this.locale })
      );

      // Rotate to match the week start
      const weekdays = repeat(weekStart)(rotateLeft)(normalizedWeekdays);

      return weekdays;
    };

    /**
     * Function was taken from the @date-io/luxon library
     * and then altered to localize based on locale.
     */
    readonly getWeekArray = (date: DateTime): DateTime[][] => {
      const weekStartDay = weekUris.indexOf(workWeekStartUri) + 1;

      const weekStart = weekstartWeekdayToLuxonWeekday(weekStartDay);

      const startOfMonth = date.startOf('month');

      let startDate = startOfMonth;

      while (startDate.weekday !== weekStart) {
        startDate = startDate.minus({ days: 1 });
      }

      const endOfMonth = date.endOf('month');
      const weekEnd = weekstartWeekdayToLuxonWeekday(
        (luxonWeekdayToWeekstartWeekday(weekStart) - 1 + 7) % 7
      );
      let endDate = endOfMonth;

      while (endDate.weekday !== weekEnd) {
        endDate = endDate.plus({ days: 1 });
      }

      const { days } = endDate.diff(startDate, 'days').toObject();
      const weeks: DateTime[][] = [];

      new Array(Math.round(days || 0))
        .fill(0)
        .map((_, i) => i)
        .map((day) => startDate.plus({ days: day }))
        .forEach((v, i) => {
          if (i === 0 || (i % 7 === 0 && i > 6)) {
            weeks.push([v]);

            return;
          }

          weeks[weeks.length - 1].push(v);
        });

      return weeks;
    };
  }
  return CustomLuxonUtils;
};
